import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

type Platform = 'macos' | 'windows' | 'linux';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: {
    code: string;
    message: string;
  };
}

interface DownloadResponse {
  downloadUrl: string;
  expiresAt: string;
  fileName: string;
  fileSize: number;
  version: string;
  sha256?: string;
  isTrial?: boolean;
}

interface VersionInfo {
  version: string;
  releaseDate: string;
  platforms: Platform[];
  isLatest: boolean;
}

interface VersionsResponse {
  productId: string;
  versions: VersionInfo[];
}

interface DownloadParams {
  productId: string;
  platform: Platform;
  version?: string;
}

/**
 * Hook to download a product artifact
 */
export function useDownload() {
  return useMutation({
    mutationFn: async (params: DownloadParams): Promise<DownloadResponse> => {
      const queryParams: Record<string, string | undefined> = {};
      if (params.version) {
        queryParams.version = params.version;
      }

      const response = await apiClient.get<ApiResponse<DownloadResponse>>(
        `/downloads/${params.productId}/${params.platform}`,
        { queryParams }
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get download URL');
      }

      return response.data;
    },
  });
}

/**
 * Hook to list available versions for a product
 */
export function useVersions(productId: string, enabled = true) {
  return useQuery({
    queryKey: ['versions', productId],
    queryFn: async (): Promise<VersionInfo[]> => {
      const response = await apiClient.get<ApiResponse<VersionsResponse>>(
        `/downloads/${productId}/versions`
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to list versions');
      }

      return response.data.versions;
    },
    enabled: enabled && !!productId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to get download info without triggering a download
 */
export function useDownloadInfo(
  productId: string,
  platform: Platform,
  version?: string,
  enabled = true
) {
  return useQuery({
    queryKey: ['download', productId, platform, version],
    queryFn: async (): Promise<DownloadResponse> => {
      const queryParams: Record<string, string | undefined> = {};
      if (version) {
        queryParams.version = version;
      }

      const response = await apiClient.get<ApiResponse<DownloadResponse>>(
        `/downloads/${productId}/${platform}`,
        { queryParams }
      );

      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to get download info');
      }

      return response.data;
    },
    enabled: enabled && !!productId && !!platform,
    staleTime: 30 * 1000, // 30 seconds (URLs expire in 1 hour)
  });
}
